import Sidebar from './sidebar';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';

export default function CandidateLayout({ children }) {
    const router = useRouter();
    const { data: session, status } = useSession();
    const [sideMenuArr, setSideMenuArr] = useState([]);
    const [isChecking, setIsChecking] = useState(true);
    const [loadingTimeout, setLoadingTimeout] = useState(false);

    // Timeout fallback - if session is loading for more than 10 seconds, stop waiting
    useEffect(() => {
        if (status === 'loading') {
            const timer = setTimeout(() => {
                console.warn('CandidateLayout: Session loading timeout reached (10s)');
                setLoadingTimeout(true);
            }, 10000); // 10 seconds

            return () => clearTimeout(timer);
        } else {
            setLoadingTimeout(false);
        }
    }, [status]);

    useEffect(() => {
        let isMounted = true;

        const checkAuth = async () => {
            // Check if session is still loading - but not indefinitely
            if (status === 'loading' && !loadingTimeout) {
                console.log('CandidateLayout: Session still loading, waiting...');
                return; // Keep isChecking as true
            }

            // If loading timeout reached, treat as unauthenticated
            if (status === 'loading' && loadingTimeout) {
                console.error('CandidateLayout: Session loading timed out, redirecting to login');
                if (isMounted) {
                    window.location.href = '/candidate-login';
                }
                return;
            }

            try {
                // Check authentication
                if (status === 'unauthenticated') {
                    console.log('CandidateLayout: Not authenticated, redirecting to login');
                    if (isMounted) {
                        window.location.href = '/candidate-login';
                    }
                    return;
                }

                // Log full session data for debugging
                console.log('CandidateLayout: Full session object:', session);
                console.log('CandidateLayout: Session status:', status);

                // Check if user is a candidate
                const userRole = session?.user?.roles?.[0] || session?.user?.roles;
                console.log('CandidateLayout: User role detected:', userRole);

                if (userRole !== 'Candidate') {
                    console.log('CandidateLayout: Not a candidate role, redirecting to home');
                    if (isMounted) {
                        window.location.href = '/';
                    }
                    return;
                }

                console.log('CandidateLayout: Candidate authenticated, processing session data');

                // Store session data in localStorage for sidebar and other components
                if (session?.user && isMounted) {
                    const user = session.user;
                    console.log('CandidateLayout: Session user data:', {
                        name: user.name || user.username,
                        email: user.email,
                        userid: user.userid || user._id,
                        hasMenubar: !!(user.menubarData || user.menubar || user.menubardata)
                    });

                    const candidateId = user.userid || user._id || '';

                    localStorage.setItem('name', user.name || user.username || '');
                    localStorage.setItem('roles', 'Candidate');
                    localStorage.setItem('userId', user.email || '');
                    localStorage.setItem('candidateId', candidateId);
                    localStorage.setItem('_id', candidateId); // For compatibility with UserProfileModal
                    localStorage.setItem('username', user.name || user.username || '');

                    // Store JWT token if available
                    if (user.token) {
                        localStorage.setItem('jwt', user.token);
                    }

                    // Get menubar data from session - check all possible field names
                    const menubarSource = user.menubarData || user.menubar || user.menubardata;

                    if (menubarSource) {
                        console.log('CandidateLayout: MenuBar data found in session');
                        localStorage.setItem('menubardata', JSON.stringify(menubarSource));

                        // Extract and set candidate menu directly from session
                        const candidateMenu = extractCandidateMenu(menubarSource);
                        if (candidateMenu && candidateMenu.length > 0) {
                            console.log('CandidateLayout: Setting candidate menu from session:', candidateMenu);
                            setSideMenuArr(candidateMenu);

                            // Prefetch menu URLs
                            candidateMenu.forEach((item) => {
                                if (item["URL"]) {
                                    try {
                                        router.prefetch(item["URL"]);
                                    } catch (prefetchError) {
                                        console.warn('Prefetch failed for:', item["URL"]);
                                    }
                                }
                            });
                        }
                    } else {
                        console.warn('CandidateLayout: No menubar data in session, creating default menu');
                        const defaultMenu = createDefaultMenu();
                        localStorage.setItem('menubardata', JSON.stringify(defaultMenu));

                        const candidateMenu = extractCandidateMenu(defaultMenu);
                        setSideMenuArr(candidateMenu);
                    }
                }

                // Successfully completed authentication check
                if (isMounted) {
                    console.log('CandidateLayout: Authentication complete, setting isChecking to false');
                    setIsChecking(false);
                }

            } catch (error) {
                console.error('CandidateLayout: Error in checkAuth:', error);
                // Set default menu on error
                const defaultMenu = createDefaultMenu();
                const candidateMenu = extractCandidateMenu(defaultMenu);
                setSideMenuArr(candidateMenu);

                // Set isChecking to false on error
                if (isMounted) {
                    console.log('CandidateLayout: Error handled, setting isChecking to false');
                    setIsChecking(false);
                }
            }
        };

        checkAuth();

        return () => {
            isMounted = false;
        };
    }, [status, session, router, loadingTimeout]);

    // Helper function to create default menu structure
    const createDefaultMenu = () => {
        return {
            dashboard: [{
                role: 'Candidate',
                name: 'Candidate',
                sideMenu: [
                    { "Label": "Dashboard", "URL": "/candidate/dashboard", "Icon": "dashboard" },
                    { "Label": "My Applications", "URL": "/candidate/myApplications", "Icon": "file-text" },
                    { "Label": "My Documents", "URL": "/candidate/myDocuments", "Icon": "files" },
                    { "Label": "My Profile", "URL": "/candidate/profile", "Icon": "user" }
                ]
            }]
        };
    };

    // Helper function to extract candidate menu from menubar data
    const extractCandidateMenu = (menubarData) => {
        try {
            if (!menubarData || !menubarData.dashboard || !Array.isArray(menubarData.dashboard)) {
                console.warn('CandidateLayout: Invalid menubar structure');
                return [];
            }

            // Look for Candidate menu in dashboard array
            for (let i = 0; i < menubarData.dashboard.length; i++) {
                const dashboardItem = menubarData.dashboard[i];

                if (dashboardItem?.role === 'Candidate' ||
                    dashboardItem?.name === 'Candidate') {

                    if (dashboardItem?.sideMenu && Array.isArray(dashboardItem.sideMenu)) {
                        console.log(`CandidateLayout: Found Candidate menu at index ${i}`);
                        return dashboardItem.sideMenu;
                    }
                }
            }

            // Fallback to first available menu
            const fallbackMenu = menubarData.dashboard[0]?.sideMenu || [];
            console.log('CandidateLayout: Using fallback menu');
            return fallbackMenu;

        } catch (err) {
            console.error('CandidateLayout: Error extracting candidate menu:', err);
            return [];
        }
    };

    // Show loading spinner while checking authentication
    if (isChecking || (status === 'loading' && !loadingTimeout)) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                fontSize: '18px',
                color: '#666'
            }}>
                <div>
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="layout">
            <div className="sidebar">
                <Sidebar sideMenuArrProp={sideMenuArr} />
            </div>
            <div className="main">
                {children}
            </div>
        </div>
    );
}
